'use client';

import { createRoute, Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { type FormEvent, Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { Button, Input, Skeleton } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { submitContact } from '@/lib/contact/submitContact';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  head: () => seoHead('/contact'),
  component: Contact,
});

type Status = 'idle' | 'submitting' | 'success' | 'error';

function ContactContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('submitting');
    setError(null);

    const result = await submitContact(
      { name, email, message, honeypot },
      import.meta.env.VITE_FORMSPREE_FORM_ID
    );

    if (result.ok) {
      setStatus('success');
    } else {
      setError(result.error);
      setStatus('error');
    }
  }

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[640px]">
            <Skeleton className="mx-auto mt-2 mb-4 h-12 w-[38%]" />
            <Skeleton className="mx-auto mb-2 h-6 w-[85%]" />
            <Skeleton className="mx-auto mb-6 h-6 w-[70%]" />
            <Skeleton className="mx-auto mb-12 h-5 w-[55%]" />
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-40 w-full rounded-md" />
              </div>
              <Skeleton className="h-11 w-full rounded-md" />
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[640px]">
          {/* Header */}
          <h1
            className="text-gradient-hero mb-4 text-center text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Let's talk.
          </h1>
          <p
            className="text-foreground/70 mb-4 text-center text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            Questions, feedback, or a deployment that needs something special.
            Tell us what you need and we will get back to you.
          </p>
          <p
            className="text-foreground/60 mb-12 text-center text-base"
            style={{ fontFamily: theme.fonts.body }}
          >
            Want hands-on help or an audit?{' '}
            <Link
              to="/consulting"
              className="underline"
              style={{ textDecorationColor: theme.colors.semantic.info }}
            >
              See consulting
            </Link>
            .
          </p>

          {status === 'success' ? (
            <div
              className="rounded-xl p-8 text-center"
              style={{
                background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
                border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
              }}
            >
              <h2
                className="mb-3 text-2xl font-bold md:text-3xl"
                style={{ fontFamily: theme.fonts.display }}
              >
                Thanks for reaching out.
              </h2>
              <p
                className="text-foreground/70"
                style={{ fontFamily: theme.fonts.body }}
              >
                We have your message and will get back to you soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  className="min-h-40"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                />
              </div>

              {/* Honeypot: hidden from people, catches form-filling bots. */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                <label htmlFor="contact-company">
                  Leave this field empty
                  <input
                    id="contact-company"
                    name="_gotcha"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>

              {error && (
                <p
                  className="text-destructive text-sm"
                  style={{ fontFamily: theme.fonts.body }}
                  role="alert"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={status === 'submitting'}
                className="w-full text-base font-semibold text-white transition-all duration-200 hover:!text-white hover:!opacity-100 hover:brightness-110"
                style={{
                  background: '#4c1d95',
                  fontFamily: theme.fonts.body,
                }}
              >
                {status === 'submitting' ? 'Sending…' : 'Send message'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}

function Contact() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[640px]">
              <Skeleton className="mx-auto mb-8 h-12 w-[60%]" />
              <Skeleton className="mx-auto mb-4 h-6 w-[80%]" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <ContactContent />
    </Suspense>
  );
}
